import { Component } from "@theme/component";

/**
 * Marken A–Z.
 *
 * Sucht innerhalb der bereits gerenderten Kacheln — kein Nachladen, weil alle
 * 244 Marken ohnehin auf der Seite stehen. Die A–Z-Leiste zeigt beim Scrollen,
 * in welchem Buchstaben man gerade ist.
 */
class MarkenAZ extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.kacheln = Array.from(this.querySelectorAll(".marken__kachel"));
    this.gruppen = Array.from(this.querySelectorAll(".marken__gruppe"));
    this.buchstaben = new Map(
      Array.from(this.querySelectorAll("a.marken__buchstabe")).map((a) => [a.dataset.buchstabe, a])
    );

    this.zaehlerVorlage = this.#zaehlerVorlage();
    this.#zaehlerSchreiben(this.kacheln.length);
    this.#positionBeobachten();
    this.#leisteMessen();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.beobachter?.disconnect();
    this.leisteBeobachter?.disconnect();
    if (this.beiGroesse) window.removeEventListener("resize", this.beiGroesse);
  }

  /* --------------------------------------------- Hoehe der A–Z-Leiste */

  /**
   * Schreibt die tatsaechliche Hoehe der Leiste als --marken-leiste-h.
   * Das Sprungziel eines Buchstabens (scroll-margin-top) muss unter Header
   * UND Leiste liegen; die Leiste ist mobil drei Zeilen hoch (177px bei
   * 390px Fensterbreite), auf Desktop eine (68px). Ein fester Wert waere
   * in der einen oder anderen Breite falsch.
   */
  #leisteMessen() {
    const leiste = this.refs.leiste;
    if (!leiste) return;

    let letzte = null;
    const schreiben = () => {
      const hoehe = Math.round(leiste.getBoundingClientRect().height);
      if (hoehe <= 0 || hoehe === letzte) return;
      letzte = hoehe;
      this.style.setProperty("--marken-leiste-h", `${hoehe}px`);
      // Die Leiste wechselt zwischen einer und drei Zeilen — das verschiebt
      // auch das Band, in dem der aktive Buchstabe bestimmt wird.
      if (this.beobachter) this.#beobachterSetzen();
    };

    schreiben();
    if ("ResizeObserver" in window) {
      this.leisteBeobachter = new ResizeObserver(schreiben);
      this.leisteBeobachter.observe(leiste);
    }
  }

  #zaehlerVorlage() {
    // Der Text steht im Schema; ohne Platzhalter genügt die reine Zahl.
    const el = this.refs.zaehler;
    return el?.dataset.vorlage || "[anzahl] Marken";
  }

  /* ------------------------------------------------------------- Suche */

  suchen(event) {
    // event.target — das Theme reicht Handler über einen globalen Listener nach.
    const begriff = event.target.value.trim().toLowerCase();

    let sichtbar = 0;
    for (const kachel of this.kacheln) {
      const treffer = begriff === "" || kachel.dataset.name.includes(begriff);
      kachel.hidden = !treffer;
      if (treffer) sichtbar++;
    }

    // Gruppen ohne Treffer ganz ausblenden, sonst bleiben leere Buchstaben stehen.
    for (const gruppe of this.gruppen) {
      const hatTreffer = gruppe.querySelector(".marken__kachel:not([hidden])") !== null;
      gruppe.hidden = !hatTreffer;
    }

    this.#zaehlerSchreiben(sichtbar);

    const nichts = this.refs.nichts;
    if (nichts) {
      nichts.hidden = sichtbar > 0;
      if (sichtbar === 0) nichts.textContent = this.dataset.leerText;
    }
  }

  #zaehlerSchreiben(anzahl) {
    const el = this.refs.zaehler;
    if (el) el.textContent = this.zaehlerVorlage.replace("[anzahl]", anzahl);
  }

  /* ----------------------------------------------------------- Gruppen */

  gruppeUmschalten(event) {
    const kopf = event.target.closest(".marken__gruppe-kopf");
    if (!kopf) return;
    const offen = kopf.getAttribute("aria-expanded") === "true";
    kopf.setAttribute("aria-expanded", offen ? "false" : "true");
  }

  /* -------------------------------------------- Position in der Leiste */

  /**
   * Das Beobachtungsband beginnt direkt UNTER Header und Leiste — beide bleiben
   * beim Scrollen stehen und verdecken mobil die oberen 268px. Ein festes Band
   * bei 15% der Fensterhoehe lag dahinter: nach einem Sprung auf „T“ blieb
   * sichtbar „S“ markiert, weil dort gemessen wurde, wo nichts zu sehen ist.
   */
  #positionBeobachten() {
    if (!("IntersectionObserver" in window) || !this.gruppen.length) return;
    this.#beobachterSetzen();

    // Das Band haengt an der Fensterhoehe (Drehen des Geraets, Adressleiste).
    this.beiGroesse = () => {
      cancelAnimationFrame(this.groessenBild);
      this.groessenBild = requestAnimationFrame(() => this.#beobachterSetzen());
    };
    window.addEventListener("resize", this.beiGroesse, { passive: true });
  }

  #beobachterSetzen() {
    this.beobachter?.disconnect();

    const kopfhoehe =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
    const leistenhoehe = this.refs.leiste?.getBoundingClientRect().height || 0;
    const oben = Math.round(kopfhoehe + leistenhoehe + 12);
    const bandhoehe = 120;
    const unten = Math.max(0, Math.round(window.innerHeight - oben - bandhoehe));

    const sichtbar = new Set();
    this.beobachter = new IntersectionObserver(
      (eintraege) => {
        for (const e of eintraege) {
          if (e.isIntersecting) sichtbar.add(e.target.dataset.gruppe);
          else sichtbar.delete(e.target.dataset.gruppe);
        }

        const kandidaten = this.gruppen.filter((g) => sichtbar.has(g.dataset.gruppe));
        if (!kandidaten.length) return; // nichts im Band: letzte Markierung stehen lassen

        // Beginnt eine Gruppe im Band, ist das die, in der man gerade steht.
        // Ohne diese Bevorzugung gewinnt die vorherige Gruppe: sie endet genau
        // auf der Bandkante, zaehlt dadurch als sichtbar und steht im Dokument
        // weiter oben — nach einem Sprung auf „T“ blieb so „S“ markiert.
        const beginnend = kandidaten.filter((g) => {
          const top = g.getBoundingClientRect().top;
          return top >= oben - 1 && top <= oben + bandhoehe;
        });
        const aktiv = beginnend.length ? beginnend[beginnend.length - 1] : kandidaten[0];

        this.buchstaben.forEach((a, b) => a.classList.toggle("is-aktiv", b === aktiv.dataset.gruppe));
      },
      { rootMargin: `-${oben}px 0px -${unten}px 0px` }
    );
    this.gruppen.forEach((g) => this.beobachter.observe(g));
  }
}

if (!customElements.get("marken-az")) {
  customElements.define("marken-az", MarkenAZ);
}
